import { observable, action, computed, makeObservable } from 'mobx';

import { getSalesforceUrl } from 'src/api';

import office from 'src/services/office';

import { executeFlowsteps, executeFlows, convertIds } from 'src/services/execution';
import { findFlowsteps } from 'shared/utils/flow';

import { Category, FlowstepType } from 'shared/utils/flowstep';
import { asArray } from 'shared/utils/utils';
import { SalesforceIdRegexp } from 'shared/utils/queryAnalysis';

import { parseCellAddress } from 'shared/excel/excel';
import { AccountType, Operation, ProviderType } from 'shared/types/flowstep';
import { Type } from 'shared/utils/flowstep';
import { matchesToProvider } from 'src/components/Flows/utils';
import { CURRENT_VERSION } from 'shared/migration/flowstep';
import { clearProviderCache } from '@v2/services/flowsteps/remote-api-provider';
import { diContainer } from "shared/infrastructure/di-container";
import { ProviderFactory } from "shared/providers/provider";


export default class OfficeStore {
  @observable initialized = false;
  @observable maintenanceMode = false;

  @computed get flowSteps() {
    const fromNewFormat = (flowstep) => {
      return {
        id: flowstep.id,
        worksheetId: flowstep.options.worksheetId,
        target: flowstep.options.target,
        name: flowstep.name,
        category: flowstep.operation === Operation.PULL ? Category.Pull : Category.Push,
        type: Type.Hubspot,
        options: {
          errorReaction: flowstep.errorReaction,
          pullMode: flowstep.options.pullMode,
          rangeToApply: flowstep.options.rangeToApply,
        },
        originalFlowstep: flowstep,
        version: flowstep.version
      }
    }

    return this.dataStore.flowSteps.map(
      flowstep => flowstep.provider
        ? fromNewFormat(flowstep)
        : flowstep
    );
  }
  @computed get flows() { return this.dataStore.flows; }
  @computed get scheduler() { return this.dataStore.scheduler; }
  @computed get actions() { return this.dataStore.actions; }
  @computed get allUserActions() { return this.dataStore.allUserActions; }

  @computed get reports() { return this.salesforceStore.reports; }
  @computed get reportsData() { return this.salesforceStore.reportsData; }
  @computed get objects() { return this.salesforceStore.objects; }
  @computed get objectsData() { return this.salesforceStore.objectsData; }

  @computed get worksheets() { return this.contextStore.worksheets; }
  @computed get activeWorksheetContext() { return this.contextStore.activeWorksheetContext; }

  @computed get activeWorksheetId() { return this.activeWorksheetContext.id; }
  @computed get currentFlowSteps() {
    return this.flowSteps.filter(i => i.worksheetId === this.activeWorksheetId);
  }

  @computed get providerFlowsteps() {
    return this.flowSteps.filter(flowstep => matchesToProvider(flowstep, this.userStore.accountType));
  }

  requestStore = null;
  userStore = null;
  salesforceStore = null;
  hubspotStore = null;
  dataStore = null;
  contextStore = null;
  coachmarkStore = null;

  constructor() {
    makeObservable(this);
  }

  linkStores(stores) {
    Object.assign(this, stores);
  }

  @action async clean() {
    clearProviderCache();
    await Promise.all([this.dataStore.clean(), this.salesforceStore.clean(), this.hubspotStore.clean(), this.contextStore.clean()]);
  }

  @action async startup() {
    //load on background
    this.loadCachedEntities();

    await Promise.all([
      // this.coachmarkStore.startup(), // it was decided to disable coachmark (XLC-1040)
      this.dataStore.startup().then(() => this.contextStore.startup())
    ]);
    this.initialized = true;
  }

  @action async loadCachedEntities() {
    const currentAccountType = this.userStore.accountType;

    if (currentAccountType === AccountType.SF) {
      await Promise.all([this.fetchRecentReports(), this.fetchObjects()]);
    }
  }

  @action async fetchRecentReports() {
    //@rollback
    // return await this.salesforceStore.fetchRecentReports();
    const providerFactory = diContainer.resolve('providerFactory');
    const provider = providerFactory.getProviderClient(ProviderType.SalesforceReport);
    return await provider.loadObjectTypes();
  }

  @action async fetchReports(data) {
    return await this.salesforceStore.fetchReports(data);
  }

  @action async fetchReportData(data) {
    return await this.salesforceStore.fetchReportData(data);
  }

  @action addLoadedReports(reports) {
    this.salesforceStore.addReports(reports);
  }

  @action getReportDataIfCached(data) {
    return this.salesforceStore.getReportDataIfCached(data);
  }

  @action async fetchReportFields(data) {
    return await this.salesforceStore.fetchReportFields(data);
  }

  @action getReportFieldsIfCached(data) {
    return this.salesforceStore.getReportFieldsIfCached(data);
  }

  @action async fetchObjects() {
    return await this.salesforceStore.fetchObjects();
  }

  @action async fetchObjectData(data) {
    return await this.salesforceStore.fetchObjectData(data);
  }

  @action async fetchObjectFields(objectName) {
    return await this.salesforceStore.fetchObjectFields(objectName);
  }

  @action async runQueries(queries, rowCountLimit = Number.POSITIVE_INFINITY) {
    return await this.salesforceStore.runQueries(queries, rowCountLimit);
  }

  // Refresh flowsteps

  @action async refreshAllSheets(category, cancelToken) {
    const flowsteps = this.filterFlowsteps(category);
    await executeFlowsteps(flowsteps, cancelToken);
  }

  @action async refreshCurrentSheet(category, cancelToken) {
    const worksheet = await office.getActiveWorksheet();
    await this.refreshSheet(category, worksheet.id, cancelToken);
  }

  @action async refreshSheet(category, worksheetId, cancelToken) {
    const flowsteps = this.filterFlowsteps(category, worksheetId);
    if (!flowsteps.length) {
      throw new Error('Active spreadsheet has no data source(s) associated with it. Run a object query to create a data source');
    }

    await executeFlowsteps(flowsteps, cancelToken);
  }

  filterFlowsteps(category, worksheetId = '') {
    const categories = asArray(category);
    const { flowsteps } = findFlowsteps(this.flows, this.flowSteps);
    const isHubspot = this.userStore.accountType === AccountType.HS;
    const result = flowsteps.filter(x =>
      (isHubspot ? x.type === FlowstepType.Hubspot : (x.type !== FlowstepType.Hubspot || x.version === CURRENT_VERSION))
      && categories.includes(x.category)
      && (!worksheetId || worksheetId === x.worksheetId));
    return result;
  }

  // TODO: Move to SalesforceStore
  async substituteQueryOnActiveWorksheet(query, queryType) {
    return await this.substituteQuery(query, queryType, this.activeWorksheetId);
  }

  // TODO: Move to SalesforceStore
  async substituteQuery(query, queryType, queryWorksheetId) {
    return await this.salesforceStore.substituteQuery(query, queryType, queryWorksheetId);
    // return await Excel.run(async (context) => {
    //   return await substituteQuery(context, query, queryWorksheetId);
    // });
  }

  isExecutionRunning = false;

  async executeFlowstep(flowstepOrFlowsteps, cancelToken) {
    if (!this.isExecutionRunning) {
      this.isExecutionRunning = true;
      try {
        await executeFlowsteps(asArray(flowstepOrFlowsteps), cancelToken);
      } finally {
        this.isExecutionRunning = false;
      }
    }
  }

  async executeFlow(flowOrFlows, cancelToken) {
    if (!this.isExecutionRunning) {
      this.isExecutionRunning = true;
      try {
        await executeFlows(asArray(flowOrFlows), cancelToken);
      } finally {
        this.isExecutionRunning = false;
      }
    }
  }

  async convertIds(cancelToken) {
    const selection = await office.getSelectedRanges();
    await convertIds(selection, cancelToken);
  }


  // TODO: Move to SalesforceStore
  @action async openInSalesforce() {
    const { values, columnIndex, rowIndex } = await office.getActiveCell();
    let [, id] = values[0][0].toString().match(SalesforceIdRegexp) || [];

    if (!id) {
      await this.contextStore.updateActiveWorksheetContext();
      const worksheet = await office.getActiveWorksheet();

      for (const flowStep of this.currentFlowSteps) {
        const target = flowStep.target || flowStep.options.target;
        const { columnIndex: columnIndex0, rowIndex: rowIndex0 } = parseCellAddress(target);
        if (columnIndex >= columnIndex0 && rowIndex >= rowIndex0) {
          if (flowStep.type === FlowstepType.Report) {
            id = flowStep.options.report_id;
            break;
          }

          if (flowStep.originalFlowstep?.provider === ProviderType.SalesforceReport) {
            const { originalFlowstep } = flowStep;
            const providerFactory = diContainer.resolve('providerFactory');
            const provider = providerFactory.getProviderClient(originalFlowstep.provider);
            if (provider) {
              const url = await provider.buildUrl({ flowstep: originalFlowstep });
              if (url?.length) {
                window.open(url, '_blank');
                return;
              }
            }
            break;
          }

          if (flowStep.type !== FlowstepType.Soql) {
            continue;
          }

          if (columnIndex < (columnIndex0 + flowStep.options.fields.length)) {
            const idFieldIndex = flowStep.options.fields.findIndex(i => i.name.toLocaleLowerCase() === 'id' && i.type == 'id');
            if (idFieldIndex >= 0) {
              const values = await office.getValuesByIndexes(worksheet, rowIndex, columnIndex0 + idFieldIndex, 1, 1);
              if (values[0][0].toString().match(SalesforceIdRegexp)) {
                id = values[0][0]; break;
              }
            }
          }
        }
      }
    }

    await this.openInSalesforceById(id);
  }

  // TODO: Move to SalesforceStore
  async openInSalesforceById(id) {
    const { url } = await getSalesforceUrl({ id }, this.userStore.token);
    window.open(url, '_blank');
  }

  async getFlowstepColumns(flowstep) {
    return await this.salesforceStore.getFlowstepColumns(flowstep);
  }
}
